import { filter, fromEvent, merge, Observable, Subject, type Subscription, take, takeUntil, tap } from 'rxjs'
import { DragVelocity, projectedDistance } from './overlay.dismiss-law'

/**
 * Swipe-to-dismiss gesture for sheet-layout overlays.
 *
 * Pointer-event based — a single pipeline covers touch, mouse, pen, and
 * any future pointer source. Without a visible drag handle, the gesture
 * only starts within the top DRAG_START_TOP_PX band of the surface (a
 * standard pull-to-dismiss convention). Escape + backdrop click cover
 * the non-pointer dismiss paths via the component's modal-tier listeners.
 *
 * RxJS-native — every async source is lifted to an Observable.
 * Thresholds are policy constants, not magic numbers.
 */

/** Distance (px) past which the gesture dismisses. */
export const DISMISS_DISTANCE_PX = 80
/** Distance as fraction of sheet height past which the gesture dismisses. */
export const DISMISS_DISTANCE_FRACTION = 0.25 // Vaul's value — feels right.
/** Travel below which a release never dismisses on speed alone. */
export const DISMISS_MIN_TRAVEL_PX = 20
/** Drag only starts if pointerdown is within this top band (unless a drag handle is used). */
export const DRAG_START_TOP_PX = 40
/** Snap-back animation duration when gesture doesn't pass threshold. */
export const SNAP_BACK_MS = 300

/**
 * Logarithmic rubber-band damping for upward (negative) drag past the
 * top detent. Pulled from Vaul — `8 * (log(v + 1) - 2)` — diminishing
 * returns that feel physical.
 *
 * Applied only to upward motion; downward drag tracks 1:1.
 */
export function logDamp(delta: number): number {
	if (delta >= 0) return delta
	const abs = -delta
	return -Math.max(0, 8 * (Math.log(abs + 1) - 2))
}

export interface SwipeInputs {
	surface: HTMLElement
	/** Optional drag handle. When provided, drag can start from anywhere on
	 *  the handle; without it, drag must start in the top DRAG_START_TOP_PX band. */
	dragHandle?: HTMLElement | null
	/** Completes the gesture stream (component disconnect / layout change). */
	until$: Observable<unknown>
	/** Asked once a pull passes the dismiss threshold. Unset allows every pull. */
	allows?: () => boolean
}

/**
 * Emits exactly once when the user commits to dismissing the sheet
 * (distance OR velocity threshold passed). Works for touch, mouse, and
 * pen via the Pointer Events API. During an in-progress drag the
 * surface's `transform` is updated directly for 1:1 tracking. On
 * release-but-not-dismissed, the surface snaps back via a CSS transition —
 * a pull `allows` refuses included, which leaves the gesture listening.
 *
 * Mounted content that owns a pointer gesture of its own — an image viewer's
 * pinch, pan, or swipe — claims the press by calling `preventDefault()` on its
 * `pointerdown`. That listener sits deeper in the composed path than the
 * surface, so it runs first in the bubble phase, and a pointerdown that arrives
 * here already prevented never starts the sheet drag.
 */
export function swipeToDismiss$(inputs: SwipeInputs): Observable<'dismiss'> {
	const { surface, dragHandle, until$, allows } = inputs

	return new Observable<'dismiss'>(subscriber => {
		const dragTarget = dragHandle ?? surface
		const stopCurrent$ = new Subject<void>()

		let dragging = false
		let activePointerId: number | null = null
		let startY = 0
		let currentDelta = 0
		// Shared with the viewer's own law, which is where the two rules that make
		// this correct are written down: an empty window is zero, and a window too
		// thin to measure is zero. Only the WINDOW is shared — the sheet keeps its
		// own distance thresholds and its own damping.
		const speed = new DragVelocity()

		const pointerDown$ = fromEvent<PointerEvent>(dragTarget, 'pointerdown').pipe(
			// Primary button / primary finger only. For touch, isPrimary also
			// covers the pinch-zoom guard (only one finger has isPrimary=true).
			filter(e => e.isPrimary && (e.pointerType !== 'mouse' || e.button === 0)),
			// Mounted content already claimed this press for a gesture of its own.
			filter(e => !e.defaultPrevented),
			// Without a dedicated drag handle, require start in the top band.
			filter(e => {
				if (dragHandle) return true
				const rect = surface.getBoundingClientRect()
				return e.clientY - rect.top <= DRAG_START_TOP_PX
			}),
			// Interactive descendants (buttons, links, form controls) own their
			// own click — starting a drag on one calls setPointerCapture on
			// dragTarget and silently swallows the click before it reaches the
			// control (e.g. a full sheet's top-bar close button, which sits
			// inside the drag-start band with no dedicated handle to avoid it).
			filter(e => {
				for (const node of e.composedPath()) {
					if (node === dragTarget) break
					if (node instanceof Element && node.matches('button, a[href], input, select, textarea, [role="button"]')) {
						return false
					}
				}
				return true
			}),
			tap(e => {
				dragging = true
				activePointerId = e.pointerId
				startY = e.clientY
				speed.reset()
				currentDelta = 0
				surface.style.transition = 'none'
				surface.style.willChange = 'transform'
				// Capture so pointermove fires even if the pointer leaves the element
				// (covers the "drag from handle, move past surface edge" case).
				try {
					;(dragTarget as Element).setPointerCapture?.(e.pointerId)
				} catch {
					// setPointerCapture can throw on detached elements; safe to ignore.
				}
			}),
		)

		const pointerMove$ = fromEvent<PointerEvent>(dragTarget, 'pointermove').pipe(
			filter(() => dragging),
			filter(e => e.pointerId === activePointerId),
			tap(e => {
				const deltaY = e.clientY - startY
				currentDelta = logDamp(deltaY)
				speed.sample(e.timeStamp, currentDelta)
				surface.style.transform = `translateY(${currentDelta}px)`
				// preventDefault is a no-op on default Pointer Events but avoids
				// accidental text selection / page scroll during the drag.
				if (e.cancelable) e.preventDefault()
			}),
		)

		// Shared by both end paths: stop tracking and give up pointer capture.
		// Never runs the dismiss-vs-snapback scoring itself.
		const releasePointer = (): void => {
			dragging = false
			const pointerId = activePointerId
			activePointerId = null
			try {
				if (pointerId !== null) (dragTarget as Element).releasePointerCapture?.(pointerId)
			} catch {
				// same reason as setPointerCapture — ignore.
			}
			surface.style.willChange = ''
		}

		const snapBack = (): void => {
			surface.style.transition = `transform ${SNAP_BACK_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
			surface.style.transform = 'translateY(0)'
		}

		const pointerUp$ = fromEvent<PointerEvent>(dragTarget, 'pointerup').pipe(
			filter(() => dragging),
			filter(e => e.pointerId === activePointerId),
			tap(e => {
				releasePointer()

				const surfaceHeight = surface.getBoundingClientRect().height
				const distanceThreshold = Math.min(DISMISS_DISTANCE_PX, surfaceHeight * DISMISS_DISTANCE_FRACTION)
				// How far the release's own speed carries it — the same projection the
				// viewer's law uses, against this surface's own threshold, so the library
				// holds one rule for what a flick is worth rather than a second px/ms
				// constant disagreeing with it. What it replaces read
				// `currentDelta / (now - startTime)` — the AVERAGE over the whole gesture
				// — so a slow pull ending in a real flick scored as slow and was refused,
				// while a finger that had already stopped still scored as moving.
				const projected = projectedDistance(currentDelta, speed.velocityAt(e.timeStamp))

				const shouldDismiss =
					currentDelta > distanceThreshold ||
					(currentDelta > DISMISS_MIN_TRAVEL_PX && projected > distanceThreshold)

				if (shouldDismiss && (allows?.() ?? true)) {
					// Finish translate off-screen for visual continuity with the dismiss
					// animation; the caller's teardown will unmount.
					surface.style.transition = `transform ${SNAP_BACK_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
					surface.style.transform = 'translateY(100%)'
					subscriber.next('dismiss')
					subscriber.complete()
				} else {
					snapBack()
				}
			}),
		)

		// pointercancel / lostpointercapture fire when WebKit reclassifies a
		// mid-pull touch as a native gesture, or pointer capture flakes — this
		// ends the drag on a partial, involuntarily-truncated delta, so it must
		// NEVER run the dismiss-vs-snapback scoring (a real swipe would silently
		// read as "below threshold" and get rejected). Snap back only; a real
		// dismiss commits exclusively through pointerup.
		const pointerAbort$ = merge(
			fromEvent<PointerEvent>(dragTarget, 'pointercancel'),
			fromEvent<PointerEvent>(dragTarget, 'lostpointercapture'),
		).pipe(
			filter(() => dragging),
			filter(e => e.pointerId === activePointerId),
			tap(() => {
				releasePointer()
				snapBack()
			}),
		)

		merge(pointerDown$, pointerMove$, pointerUp$, pointerAbort$)
			.pipe(takeUntil(merge(stopCurrent$, until$)))
			.subscribe()

		return () => {
			stopCurrent$.next()
			stopCurrent$.complete()
			surface.style.transition = ''
			surface.style.transform = ''
			surface.style.willChange = ''
		}
	}).pipe(take(1))
}

/**
 * Drag-to-reposition for `ShowOptions.drag`.
 *
 * Distinct from `swipeToDismiss$` above in every way the job differs: it
 * starts only from a dedicated handle (never the whole surface, so it can
 * never race a click on the mounted content's own controls), it moves on
 * both axes via `transform: translate()` rather than one axis toward a
 * dismiss, it never snaps back on release, and it answers arrow keys on the
 * handle the same way it answers a pointer — so the capability has a
 * keyboard path. `onDragStart` fires exactly once, the first time either
 * input actually moves the surface, for the caller to tear down live anchor
 * tracking (see `ShowOptions.drag`'s doc for why).
 */

/** Pointer movement past which a pointerdown on the handle counts as a drag
 *  rather than a stray tap — mirrors `schmancy-window`'s own threshold. */
export const DRAG_REPOSITION_THRESHOLD_PX = 5
/** Arrow-key nudge step, and the Shift-held larger step. */
export const DRAG_REPOSITION_KEY_STEP_PX = 8
export const DRAG_REPOSITION_KEY_STEP_LARGE_PX = 32

export interface DragToRepositionInputs {
	surface: HTMLElement
	/** The dedicated grab affordance — drag never starts anywhere else on
	 *  the surface, so it can never hijack a click on the mounted content's
	 *  own interactive descendants. */
	handle: HTMLElement
	/** Fires exactly once, synchronously, the first time a pointer-drag
	 *  crosses `DRAG_REPOSITION_THRESHOLD_PX` or an arrow key nudges the
	 *  surface — the caller's hook to freeze live anchor tracking. */
	onDragStart?: () => void
	/** Completes the gesture stream (component disconnect / overlay close). */
	until$: Observable<unknown>
}

/**
 * Wires the handle for the surface's whole open lifetime — unlike
 * `swipeToDismiss$`'s one-shot dismiss, reposition can happen any number of
 * times, so this never completes on its own. The caller composes it with
 * `takeUntil(until$)`.
 */
export function dragToReposition$(inputs: DragToRepositionInputs): Observable<never> {
	const { surface, handle, onDragStart, until$ } = inputs

	return new Observable<never>(() => {
		let dragging = false
		let activePointerId: number | null = null
		let startX = 0
		let startY = 0
		let baseX = 0
		let baseY = 0
		let dx = 0
		let dy = 0
		let armed = false
		let dragStartNotified = false

		const notifyDragStart = (): void => {
			if (dragStartNotified) return
			dragStartNotified = true
			onDragStart?.()
		}

		const applyTransform = (): void => {
			surface.style.transform = `translate(${dx}px, ${dy}px)`
		}

		const pointerDown$ = fromEvent<PointerEvent>(handle, 'pointerdown').pipe(
			filter(e => e.isPrimary && (e.pointerType !== 'mouse' || e.button === 0)),
			tap(e => {
				dragging = true
				armed = false
				activePointerId = e.pointerId
				startX = e.clientX
				startY = e.clientY
				baseX = dx
				baseY = dy
			}),
		)

		const pointerMove$ = fromEvent<PointerEvent>(handle, 'pointermove').pipe(
			filter(() => dragging),
			filter(e => e.pointerId === activePointerId),
			tap(e => {
				const moveX = e.clientX - startX
				const moveY = e.clientY - startY
				if (!armed) {
					if (Math.hypot(moveX, moveY) < DRAG_REPOSITION_THRESHOLD_PX) return
					armed = true
					surface.style.transition = 'none'
					handle.classList.add('cursor-grabbing')
					// Capture only once a real drag is confirmed — same reasoning as
					// `pin.ts`: Chromium retargets the pointerup-derived `click` to
					// whichever element holds capture at pointerup time, even if
					// capture is released synchronously beforehand. Capturing eagerly
					// on pointerdown would swallow a zero-movement tap's click.
					try {
						handle.setPointerCapture?.(e.pointerId)
					} catch {
						// setPointerCapture can throw on detached elements; safe to ignore.
					}
					notifyDragStart()
				}
				dx = baseX + moveX
				dy = baseY + moveY
				applyTransform()
				if (e.cancelable) e.preventDefault()
			}),
		)

		const releasePointer = (): void => {
			dragging = false
			armed = false
			handle.classList.remove('cursor-grabbing')
			const pointerId = activePointerId
			activePointerId = null
			try {
				if (pointerId !== null) handle.releasePointerCapture?.(pointerId)
			} catch {
				// same reason as setPointerCapture — ignore.
			}
		}

		// pointerup / pointercancel / lostpointercapture all just end the
		// gesture here — unlike swipeToDismiss$, there is no dismiss-vs-snapback
		// scoring: the surface stays exactly where the last applied transform
		// left it, whichever of the three ended the drag.
		const pointerEnd$ = merge(
			fromEvent<PointerEvent>(handle, 'pointerup'),
			fromEvent<PointerEvent>(handle, 'pointercancel'),
			fromEvent<PointerEvent>(handle, 'lostpointercapture'),
		).pipe(
			filter(() => dragging),
			filter(e => e.pointerId === activePointerId),
			tap(() => releasePointer()),
		)

		const keydown$ = fromEvent<KeyboardEvent>(handle, 'keydown').pipe(
			filter(e => e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight'),
			tap(e => {
				e.preventDefault()
				notifyDragStart()
				const step = e.shiftKey ? DRAG_REPOSITION_KEY_STEP_LARGE_PX : DRAG_REPOSITION_KEY_STEP_PX
				if (e.key === 'ArrowLeft') dx -= step
				if (e.key === 'ArrowRight') dx += step
				if (e.key === 'ArrowUp') dy -= step
				if (e.key === 'ArrowDown') dy += step
				applyTransform()
			}),
		)

		const sub = merge(pointerDown$, pointerMove$, pointerEnd$, keydown$)
			.pipe(takeUntil(until$))
			.subscribe()

		return () => {
			sub.unsubscribe()
			surface.style.transition = ''
			handle.classList.remove('cursor-grabbing')
		}
	})
}

/* ======================================================================= *
 *                     trailing-click suppression                            *
 * ======================================================================= */

let mobileSuppression: Subscription | undefined

/**
 * Suppress a trigger's trailing click. With a pointer, consume the whole mobile
 * gesture instead: capture its release and click even after the host is removed.
 * Pointer identity excludes other fingers; cancellation or a new primary press
 * releases the guard. Keyboard clicks are independent of the guarded pointer.
 */
export function suppressNextClick(pointer?: PointerEvent): void {
	if (typeof document === 'undefined') return
	// The mobile guard owns the whole gesture independently of the closing host.
	if (pointer) {
		mobileSuppression?.unsubscribe()
		pointer.preventDefault()
		pointer.stopImmediatePropagation()
		const ended$ = new Subject<void>()
		mobileSuppression = merge(
			fromEvent<PointerEvent>(document, 'pointerup', { capture: true }).pipe(
				filter(event => event.pointerId === pointer.pointerId),
				tap(event => {
					event.preventDefault()
					event.stopImmediatePropagation()
				}),
			),
			fromEvent<MouseEvent>(document, 'click', { capture: true }).pipe(
				filter(event => (event instanceof PointerEvent ? event.pointerId === pointer.pointerId : event.detail > 0)),
				tap(event => {
					event.preventDefault()
					event.stopImmediatePropagation()
					ended$.next()
				}),
			),
			fromEvent<PointerEvent>(document, 'pointercancel', { capture: true }).pipe(
				filter(event => event.pointerId === pointer.pointerId),
				tap(() => ended$.next()),
			),
			fromEvent<PointerEvent>(document, 'pointerdown', { capture: true }).pipe(
				filter(event => event.isPrimary),
				tap(() => ended$.next()),
			),
		)
			.pipe(takeUntil(ended$))
			.subscribe()
		return
	}
	merge(
		fromEvent<MouseEvent>(document, 'click', { capture: true }).pipe(
			tap(e => {
				e.preventDefault()
				e.stopPropagation()
				e.stopImmediatePropagation()
			}),
		),
		fromEvent<PointerEvent>(document, 'pointerdown', { capture: true }),
	)
		.pipe(take(1))
		.subscribe()
}
